// Behavior-level content. Describes the readouts the analyzer produces and how
// to act on them — NOT the scoring weights, thresholds, fraud-check rules, or
// the internal DSP rule set.

export default {
  title: 'Bid Request Analyzer',
  summary:
    'Paste an oRTB bid request and get a full read: structure and privacy compliance, identity signals, supply-chain authorization, invalid-traffic risk, DSP compatibility, and an overall quality score with the revenue levers to fix.',

  sections: [
    {
      h: 'What this tool does',
      p: [
        'Validates the request against oRTB 2.5 / 2.6 and surfaces structural errors and warnings, then layers several reads on top of it.',
        "It breaks down the privacy signals present — consent, US privacy / GPP, COPPA, limit-ad-tracking — and, based on the request's geo, tells you which signals are expected and which are missing. It reads the identity signals (device ID and universal IDs such as UID2, ID5, LiveRamp) and rates how addressable the request is.",
        "It validates the supply chain end to end — including authorization against the publisher's ads.txt / app-ads.txt, the same way the Schain Validator does — and flags invalid-traffic risk from internal contradictions in the request itself (device/OS mismatches, impossible hardware combinations, spoofed bundle or store URLs, bot user agents), with no post-bid data required.",
        'Finally it shows a DSP compatibility read — for major buyers, whether the request would likely be bid on, deprioritized, or rejected, and why — estimates the revenue impact of what\u2019s missing, and rolls everything into an overall quality score and grade.',
      ],
    },
    {
      h: 'When to use it',
      list: [
        "A specific publisher's or SSP's requests are underperforming and you want to see what's structurally wrong or missing.",
        "You're debugging why a particular DSP isn't bidding on your inventory.",
        'You want a fast quality read on a sample request before scaling a new supply integration.',
        'You suspect spoofed or invalid traffic and want the request checked for internal contradictions.',
      ],
    },
    {
      h: 'How to use it',
      steps: [
        '**Paste the bid request JSON.** That\u2019s the only required input.',
        '**Read the quality score first** for the headline, then drill into whichever sections scored low.',
        '**Work the revenue levers.** The impact section lists what\u2019s missing — no addressable ID, missing geo, non-secure inventory, and so on — with the fix for each.',
        '**Check the DSP read** if one buyer is the concern; it shows the critical gaps that would make that DSP skip the request.',
        '**Treat invalid-traffic flags as signals, not verdicts** — they point at inconsistencies worth investigating, not a confirmed ruling.',
      ],
    },
    {
      callout:
        'A zeroed-out device ID on iOS is usually correct, not fraud — it\u2019s what the OS sends when the user denies tracking. The tool treats that as compliant and only flags a zero ID when there\u2019s no opt-out reason for it.',
    },
  ],

  faqs: [
    {
      q: 'The quality score feels harsh.',
      a: 'It reflects what buyers actually look for — identity, geo, the secure flag, a clean supply chain, content categories. Each missing piece pulls the score down because it reduces what DSPs will pay. The breakdown shows where the points went.',
    },
    {
      q: 'It says a DSP would likely reject, but they buy my traffic.',
      a: 'The read is per-request, based on this one sample. A live campaign may have allowances, or the buyer may accept the request through a different path. Use it to spot the missing signals, not as a guarantee.',
    },
    {
      q: 'Invalid-traffic flags on traffic I trust?',
      a: "These are consistency checks within the request — e.g. an OS that doesn't match the user agent. Legitimate traffic can trip one if an SSP rewrites fields. Investigate the specific contradiction; it's pointing at a data-quality issue even when it isn't fraud.",
    },
    {
      q: 'Where does it get the publisher domain for the authorization check?',
      a: "From the app's store URL, or the site context for web. If that can't be resolved, the supply-chain authorization part is skipped while everything else still runs.",
    },
  ],
};
